import React from 'react';

import Checkbox from 'antd/lib/checkbox';

import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import { BaseParams } from '@common/react/objects/BaseParams';
import { WithId } from '@common/typescript/objects/WithId';
import SimpleLoader from '@common/react/components/UI/SimpleLoader/SimpleLoader';
import { ItemsProvider, PaginationOptions } from '@common/react/components/Core/ItemsProvider/ItemsProvider';

import '@common/react/scss/components/searchWithCheckboxes.scss';

interface Props<T extends WithId> {
	type: string;
	onChange: (values: Array<number>) => void;
	loadRequest?: string;
	values?: Array<number>;
	initItems?: Array<T>;
	className?: string;
	additionalParams?: BaseParams;
	renderName?: (item: T) => React.ReactNode;
	pagination?: PaginationOptions;
	title?: string;
	titleClassName?: string;
}

const SearchWithCheckboxes: <T extends WithId>(props: Props<T>) => React.ReactElement = <T extends WithId, >(props) => {
	const {
		type,
		className,
		initItems,
		additionalParams,
		values = [],
		renderName = (item) => item.name,
		onChange,
		loadRequest,
		pagination = { pageSize: 50, current: 1 },
		title = '',
		titleClassName = '',
	} = props;
	const [selectedIds, setSelectedIds] = React.useState(() => {
		const obj = {};
		values?.forEach((id) => {
			obj[id] = 1;
		});
		return obj;
	});
	const handleCheck = (e) => {
		const id = e.target.name;
		if (selectedIds[id]) {
			delete selectedIds[id];
		} else {
			selectedIds[id] = +id;
		}
		setSelectedIds({ ...selectedIds });
		onChange(Object.keys(selectedIds).map((item) => +item));
	};

	React.useEffect(() => {
		if (`${values?.map((el) => +el).sort()}` !== `${Object.keys(selectedIds).map((item) => +item).sort()}`) {
			const newValues = values.filter((item) => (values || []).includes(+item.id));
			const obj = {};
			newValues.forEach((item) => {
				obj[item.id] = 1;
			});
			setSelectedIds(obj);
		}
	}, [`${values}`]);
	const hasSelected = React.useMemo(() => {
		return !!Object.keys(selectedIds).length;
	}, [selectedIds]);
	const id = `${title}${type}checkboxes-search`;

	return <div className={`search-with-checkboxes ${className}`}>
		<div className="search-with-checkboxes-title">
			<label className={titleClassName} htmlFor={id}>
				{title}
			</label>
			{hasSelected ? (
				<button
					type="button"
					className="btn btn-sm search-with-checkboxes-clear"
					onClick={() => {
						setSelectedIds({});
						onChange([]);
					}}
				>
					Clear
				</button>
			) : null}
		</div>
		<ItemsProvider
			pagination={pagination}
			type={type}
			loadRequest={loadRequest}
			items={initItems}
			skipInitLoad={!!initItems}
			filters={{
				...additionalParams,
			}}
		>
			{({ state: { items, loading, filters }, actions: { handleChange } }) => <>
				<div className="is-relative">
					<SimpleSearchInput
						value={filters.text}
						onChange={handleChange}
						withoutForm
						otherInputProps={{
							id: `${title}${type}checkboxes-search`,
						}}
					/>
					{loading ? <SimpleLoader /> : null}
				</div>
				<ul className="checkbox-list">
					{items.map((item) => <li key={item.id}>
						<Checkbox
							name={item.id.toString()}
							checked={!!selectedIds[item.id]}
							onChange={handleCheck}
						>
							{renderName(item)}
						</Checkbox>
					</li>)}
				</ul>
			</>}
		</ItemsProvider>
	</div>;
};

export default SearchWithCheckboxes;
